// @ts-nocheck
/* eslint-disable */
// 兼容 shim：确保 window.icons / window.UtilsModule / window.i18nModule 等已就绪
// 后续会逐步迁移到显式 import
import * as React from 'react';
import {
  ActionButton,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Progress,
  Row,
  Space,
  Statistic,
  Tabs,
  Typography,
  message,
} from '@ui';
import '@lib/api';
import '@lib/auth';
import '@lib/brand';
import '@lib/utils';
import '@lib/i18n';
import '@ui';
const { icons } = window as any;
// 密钥管理已统一到 Dashboard 的「密钥管理」界面（src/features/install-token/KeyManagement.tsx），
// 含「安装令牌」与「堡垒密钥」两个页卡。概览页不再渲染任何密钥管理组件
// （AgentKeyManagement / ProxyKeyManagement 组件文件仍保留在 features 目录，便于日后恢复）。

/**
 * Overview Component
 * 概览页面组件
 *
 * 功能：
 * - 用户统计展示（在线设备、加速流量余额）
 * - 24小时流量统计（上传/下载）
 * - 修改密码功能
 * - 自动刷新统计数据（30秒）
 */

('use strict');

const { API_BASE, apiRequest } = window;
const { useState, useEffect } = React;

const { Title, Text, Paragraph } = Typography;
const { ApiOutlined, ThunderboltOutlined, ArrowUpOutlined, ArrowDownOutlined } = window.icons;
const { formatBytes } = window.UtilsModule;
const t = window.i18nModule ? window.i18nModule.t : (key, params) => key;

function Overview({ user }) {
  const [changePasswordModalVisible, setChangePasswordModalVisible] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    onlineDevices: 0,
    boostBalance: 0,
    boostEnabled: false,
    upload24h: 0,
    download24h: 0,
    llmReqCurr: 0,
    llmReqLimit: 1000,
    llmTokCurr: 0,
    llmTokLimit: 2000000,
  });

  // 加载统计数据
  useEffect(() => {
    loadStatistics();
    const interval = setInterval(loadStatistics, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStatistics = async () => {
    try {
      const {
        response: devResponse,
        data: devData,
        unauthorized: devUnauth,
      } = await apiRequest(`${API_BASE}/devices`);
      if (devUnauth) return;

      if (devResponse.ok && devData.devices) {
        const onlineCount = devData.devices.filter((d) => d.is_online).length;
        setStatistics((prev) => ({ ...prev, onlineDevices: onlineCount }));
      }

      const { data: configData } = await apiRequest(`${API_BASE}/bandwidth/config`);
      if (configData && configData.code === 0 && configData.data) {
        setStatistics((prev) => ({
          ...prev,
          boostBalance: configData.data.boost_balance_bytes || 0,
          boostEnabled: configData.data.boost_enabled || false,
        }));
      }

      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const { data: summaryData } = await apiRequest(
        `${API_BASE}/bandwidth/summary?start=${yesterday.toISOString()}&end=${now.toISOString()}`,
      );
      if (summaryData && summaryData.code === 0 && summaryData.data?.historical_stats) {
        setStatistics((prev) => ({
          ...prev,
          upload24h: summaryData.data.historical_stats.bytes_out || 0,
          download24h: summaryData.data.historical_stats.bytes_in || 0,
        }));
      }

      const { data: llmData } = await apiRequest(`${API_BASE}/llm/usage`);
      if (llmData && llmData.code === 200 && llmData.data) {
        setStatistics((prev) => ({
          ...prev,
          llmReqCurr: llmData.data.req_curr || 0,
          llmReqLimit: llmData.data.req_limit_qpd || 1000,
          llmTokCurr: llmData.data.tok_curr || 0,
          llmTokLimit: llmData.data.tok_limit_tpd || 2000000,
        }));
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const formatBytesLocal = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleChangePassword = async (values) => {
    if (values.new_password !== values.confirm_password) {
      message.error(t('overview.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      const { response, data, unauthorized } = await apiRequest(
        `${API_BASE}/user/change-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            old_password: values.old_password,
            new_password: values.new_password,
          }),
        },
      );
      if (unauthorized) return;

      if (response.ok && data.code === 200) {
        message.success(t('overview.passwordChanged'));
        changePasswordForm.resetFields();
        setChangePasswordModalVisible(false);
        setTimeout(() => {
          // 密码修改成功后强制重新登录（服务端会清除 Cookie）
          if (window.AuthModule) {
            window.AuthModule.logout();
          } else {
            window.location.href = '/';
          }
        }, 1500);
      } else {
        message.error(data.message || t('overview.passwordChangeFailed'));
      }
    } catch (error) {
      message.error(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 统计卡片 */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={12} lg={6}>
            <Card>
              <Statistic
                title={t('overview.onlineDevices')}
                value={statistics.onlineDevices}
                prefix={<ApiOutlined />}
                valueStyle={{ color: '#22c55e' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={12} lg={6}>
            <Card>
              <Statistic
                title={t('overview.boostBalance')}
                value={formatBytesLocal(statistics.boostBalance)}
                prefix={<ThunderboltOutlined />}
                valueStyle={{
                  color: statistics.boostEnabled ? '#22c55e' : '#ef4444',
                }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={12} lg={6}>
            <Card>
              <Statistic
                title={t('overview.totalUpload24h')}
                value={formatBytesLocal(statistics.upload24h)}
                prefix={<ArrowUpOutlined />}
                valueStyle={{ color: '#3b82f6' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={12} lg={6}>
            <Card>
              <Statistic
                title={t('overview.totalDownload24h')}
                value={formatBytesLocal(statistics.download24h)}
                prefix={<ArrowDownOutlined />}
                valueStyle={{ color: '#8b5cf6' }}
              />
            </Card>
          </Col>
        </Row>

        {/* AI 使用统计 */}
        <Card title="💬 AI 使用统计" size="small">
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={12} md={12} lg={6}>
              <Statistic
                title={t('overview.aiTodayRequests')}
                value={statistics.llmReqCurr}
                suffix={'/ ' + statistics.llmReqLimit + ' ' + t('overview.request')}
                valueStyle={{ color: statistics.llmReqCurr >= statistics.llmReqLimit ? '#ef4444' : '#22c55e' }}
              />
              <Progress
                percent={Math.min(100, Math.round((statistics.llmReqCurr / statistics.llmReqLimit) * 100))}
                size="small"
                status={statistics.llmReqCurr >= statistics.llmReqLimit ? 'exception' : 'active'}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={6}>
              <Statistic
                title={t('overview.aiTodayTokens')}
                value={statistics.llmTokCurr}
                suffix={'/ ' + statistics.llmTokLimit + ' ' + t('overview.token')}
                valueStyle={{ color: statistics.llmTokCurr >= statistics.llmTokLimit ? '#ef4444' : '#22c55e' }}
              />
              <Progress
                percent={Math.min(100, Math.round((statistics.llmTokCurr / statistics.llmTokLimit) * 100))}
                size="small"
                status={statistics.llmTokCurr >= statistics.llmTokLimit ? 'exception' : 'active'}
              />
            </Col>
          </Row>
        </Card>

        {/* 账号信息 */}
        <Card title={t('overview.accountInfo')}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <p>
                {t('overview.username')}: {user?.username}
              </p>
              <p>
                {t('overview.email')}: {user?.email}
              </p>
              <p>
                {t('overview.registerTime')}:{' '}
                {user?.created_at ? new Date(user.created_at).toLocaleString('zh-CN') : '-'}
              </p>
            </div>
            <div>
              <Title level={5}>{t('overview.accountSecurity')}</Title>
              <ActionButton
                action="password"
                variant="primary"
                onClick={() => setChangePasswordModalVisible(true)}
              >
                {t('overview.changePassword')}
              </ActionButton>
            </div>
          </Space>
        </Card>

      </Space>

      {/* 修改密码对话框 */}
      <Modal
        title="修改密码"
        open={changePasswordModalVisible}
        onCancel={() => {
          setChangePasswordModalVisible(false);
          changePasswordForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={changePasswordForm} onFinish={handleChangePassword} layout="vertical">
          <Form.Item
            label="原密码"
            name="old_password"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password placeholder="请输入原密码" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="new_password"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' },
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6个字符）" />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            name="confirm_password"
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4 text-xs text-yellow-500">
            <div>⚠️ 提示：</div>
            <div>• 修改密码后需要重新登录</div>
            <div>• 请确保记住新密码</div>
          </div>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <ActionButton
                action="cancel"
                onClick={() => {
                  setChangePasswordModalVisible(false);
                  changePasswordForm.resetFields();
                }}
              >
                {t('common.cancel')}
              </ActionButton>
              <ActionButton
                action="confirm"
                variant="primary"
                htmlType="submit"
                loading={loading}
              >
                {t('common.confirm')}
              </ActionButton>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// 导出到全局
export { Overview };
